/**
 * BrowserWorkSessionPanel — the operator-facing cockpit for a Digital Employee's browser work
 * sessions, mounted inside the employee workspace (DIGEMP-BROWSER-WORK-SESSION-FRONTEND-049).
 *
 * Contract family this panel reads (tolerant, add-only — nothing here is a mock, nothing here
 * grants anything):
 * - Authority/execution boundary: GET /api/connections/health → `browser_worker`
 *   (connections.browser_worker_capability() — DIGEMP-BROWSER-WORK-REQUEST-BACKEND-022):
 *   {request_supported, execution_available, blocked_reason, allowed_systems, approval_required,
 *   detail}. An absent key means the server did not report a browser adapter: the panel
 *   fails closed — no system is presented as executable and it says exactly that. A boundary whose
 *   execution_available is not a reported boolean is likewise unknown, never shown as blocked —
 *   the receipt-level pair (available / requires_approval) lives on each session's execution
 *   block, a different contract, and is never read as the health capability.
 *   approval_permission / tool_policy are tolerated optional extensions when a server reports them,
 *   and so is dev_fixture_transport (the bounded DEV/lab loopback transport, FULL-0105): it is
 *   reported separately from execution_available and is never folded into it — a real transport on
 *   a read-only DEV fixture is not live execution against a customer target.
 *   Read-only vs write readiness (DIGEMP-FINISH-BROWSER-WRITE-UX-162): execution_available is the
 *   LIVE READ-ONLY usability — the live path runs read-only sessions only, and the live write arm
 *   is a recorded constant (browser_work_executor.REASON_LIVE_WRITE_ARM_UNAVAILABLE /
 *   WRITE_COMPENSATION_BOUNDARY). The panel mirrors those two recorded boundaries as static,
 *   fail-closed statements: a write against a real target is never rendered as available, and
 *   rollback is never rendered as ready — a state-changing action's only reversal is manual.
 *   Nothing here enables anything; a session's own stored execution facts (success / verification
 *   verdict / refusal reason) drive the per-session outcome strip, and unknown combinations render
 *   as unknown, never as success and never as "nothing happened".
 * - Sessions: the workspace payload key family `browser_work_sessions` | `browser_work_session` |
 *   `browser_sessions` | `browser_work` (array | {sessions:[…]} | single record). A record follows
 *   the BrowserWorkRequestReceipt family (mode:"browser", api_fallback, state, employee/ticket/
 *   customer refs, objective{text,redactions}, allowed_system, execution{…}, requested_by/at)
 *   plus optional session extensions (stage|current_stage, stages[], run_id|run{id,status},
 *   conversation_id, waiting_for|waiting{for}, handoff|handoff_readiness, updated_at,
 *   evidence[{label,value,at}]). An absent source is UNKNOWN (the panel must not claim there are
 *   no sessions); a present empty list is genuinely none.
 * - Controls reuse only already-authorized surfaces: stop = POST /api/agent-runs/{run_id}/stop
 *   (backend gate `_approve` → agents_approve, mirroring AgentRunDetailPage's control), handoff /
 *   takeover = links to the existing run page and conversation handoff surface; attended remote
 *   takeover stays the existing TeamViewer mechanism. No new execution path exists here.
 *
 * Rendering rules: only whitelisted scalar fields are rendered (bounded, server-redacted echoes);
 * raw payloads, run contexts, tool payloads, logs and anything secret-shaped are never UI. Every
 * stage, waiting state or readiness the panel cannot ground in reported evidence is shown as
 * unknown — never inferred, never silently dropped.
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { agentRunsApi, connectionsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import { browserCapabilityBlockedLabel } from "./browserCapabilityVocabulary";
import { browserExecutionReasonLabel } from "./browserExecutionReasonVocabulary";

// Mirrors the backend's _STOPPABLE_STATES (routers/agent_runs.py) for the run bound to a session.
const RUN_STOPPABLE = new Set([
  "pending", "analyzing", "diagnosing", "connection_active",
  "waiting_for_customer_connection", "waiting_for_customer_answer",
  "awaiting_technician_review", "awaiting_approval",
]);

// Stage vocabulary → operator-facing Hebrew. Unknown literals are surfaced as-is (bounded) and
// marked unknown — never silently mapped onto a known stage.
const STAGE_META = {
  submitted: { label: "נשלחה", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  requested: { label: "נשלחה", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  queued: { label: "בתור", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  policy_check: { label: "בדיקת מדיניות", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  awaiting_approval: { label: "ממתין לאישור", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  waiting_for_approval: { label: "ממתין לאישור", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  pending_approval: { label: "ממתין לאישור", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "אושרה", cls: "bg-green-50 text-green-700 border-green-200" },
  verified: { label: "אומת", cls: "bg-green-100 text-green-700 border-green-200" },
  rejected: { label: "נדחתה", cls: "bg-red-100 text-red-700 border-red-200" },
  // The session-level stage says the session was blocked — never why. The cause travels
  // separately (execution.blocked_reason) and a session can be blocked for approvals, tamper
  // or expiry, not only for a missing adapter.
  blocked: { label: "נחסמה", cls: "bg-red-50 text-red-700 border-red-200" },
  execution_blocked: { label: "נחסמה", cls: "bg-red-50 text-red-700 border-red-200" },
  connecting: { label: "מתחבר", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  starting: { label: "מתחבר", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  running: { label: "בעבודה", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  working: { label: "בעבודה", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  active: { label: "בעבודה", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  in_progress: { label: "בעבודה", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  waiting_for_user: { label: "ממתין לפעולת משתמש", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  awaiting_user: { label: "ממתין לפעולת משתמש", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  waiting_user: { label: "ממתין לפעולת משתמש", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  waiting_for_credential: { label: "ממתין למסירת אישורי התחברות", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  waiting_for_connection: { label: "ממתין לחיבור", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  waiting_for_customer: { label: "ממתין ללקוח", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  handed_off: { label: "הועבר לטכנאי", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  handed_off_to_human: { label: "הועבר לטכנאי", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  takeover: { label: "הועבר לטכנאי", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  completed: { label: "הושלם", cls: "bg-green-100 text-green-700 border-green-200" },
  done: { label: "הושלם", cls: "bg-green-100 text-green-700 border-green-200" },
  succeeded: { label: "הושלם", cls: "bg-green-100 text-green-700 border-green-200" },
  stopped: { label: "נעצר", cls: "bg-slate-200 text-slate-700 border-slate-300" },
  cancelled: { label: "נעצר", cls: "bg-slate-200 text-slate-700 border-slate-300" },
  canceled: { label: "נעצר", cls: "bg-slate-200 text-slate-700 border-slate-300" },
  failed: { label: "נכשל", cls: "bg-red-100 text-red-700 border-red-200" },
  error: { label: "נכשל", cls: "bg-red-100 text-red-700 border-red-200" },
};

// verified/blocked/execution_blocked are the bounded fallback session's own terminal states
// (app.browser_work.execution): a finished session offers no controls here.
const TERMINAL_STAGES = new Set(["rejected", "completed", "done", "succeeded", "stopped", "cancelled", "canceled", "failed", "error", "verified", "blocked", "execution_blocked"]);
const WAITING_KINDS = { awaiting_approval: "approval", waiting_for_approval: "approval", pending_approval: "approval", waiting_for_user: "user", awaiting_user: "user", waiting_user: "user", waiting_for_connection: "connection", waiting_for_customer: "customer", waiting_for_credential: "credential", approval: "approval", user: "user", connection: "connection", customer: "customer", credential: "credential" };
const WAITING_HE = { approval: "ממתין לאישור אנושי", user: "ממתין לפעולת משתמש/מפעיל", connection: "ממתין לחיבור", customer: "ממתין ללקוח", credential: "ממתין למסירת אישורי התחברות" };

// Mirrors the recorded execution-boundary constants (app.services.browser_work_executor): the
// live write arm is unavailable by code in every configuration, and a state-changing action has
// no automated rollback / compensating action. These strings are rendered as limitations only —
// never as a capability and never as a switch.
const REASON_LIVE_WRITE_ARM_UNAVAILABLE = "live_browser_write_arm_unavailable";
const REASON_POSTCONDITION_MISMATCH = "postcondition_read_back_mismatch";
const VERIFICATION_POSTCONDITION_READ_BACK = "postcondition_read_back";
const VERIFICATION_PROVIDER_RUN = "provider_run_success";
const WRITE_BOUNDARY_LINE = "כתיבה מול יעד אמיתי: אינה זמינה בכל תצורה — מסלול הכתיבה החי לא קיים.";
const ROLLBACK_BOUNDARY_LINE = "שחזור אוטומטי: לא קיים — היפוך שינוי (אם יידרש) הוא ידני בלבד.";

const norm = (value) => (typeof value === "string" && value.trim() ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : null);
const boundedText = (value, max) => (typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null);
const boundedNumber = (value) => (typeof value === "number" && Number.isFinite(value) ? value : null);

function fmt(iso) {
  if (typeof iso !== "string" || !iso) return null;
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return null; }
}

// Tolerant session source: the workspace payload's browser-work key family. `sessions: null`
// means the source was not reported at all (unknown ≠ empty).
function extractSessions(workspace) {
  if (!workspace || typeof workspace !== "object") return { sessions: null };
  const candidates = [workspace.browser_work_sessions, workspace.browser_work_session, workspace.browser_sessions, workspace.browser_work];
  for (const candidate of candidates) {
    if (candidate == null) continue;
    const list = Array.isArray(candidate) ? candidate
      : Array.isArray(candidate.sessions) ? candidate.sessions
      : (typeof candidate === "object" && (candidate.request_id || candidate.id || candidate.stage || candidate.state)) ? [candidate]
      : null;
    if (list) return { sessions: list.filter((s) => s && typeof s === "object") };
  }
  return { sessions: null };
}

function sessionStage(session) {
  return norm(session.stage) || norm(session.current_stage) || norm(session.state) || norm(session.status);
}

function runRefOf(session) {
  const runId = boundedText(session.run_id, 64) || boundedText(session.run?.id, 64);
  const runStatus = norm(session.run?.status) || norm(session.run_status);
  return { runId, runStatus };
}

function StageChip({ stage }) {
  const meta = STAGE_META[stage];
  if (meta) {
    return <span className={`text-[11px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${meta.cls}`}>{meta.label}</span>;
  }
  if (!stage) {
    return <span data-testid="bws-stage-unknown" className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200 whitespace-nowrap">שלב לא דווח</span>;
  }
  return <span data-testid="bws-stage-unknown" className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200 whitespace-nowrap">שלב לא מזוהה: <span dir="ltr">{stage.slice(0, 40)}</span></span>;
}

function BoundarySection({ boundary, healthFailed, healthLoading }) {
  if (healthLoading) {
    return (
      <p data-testid="bws-boundary" className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
        בודק את מצב מתאם הדפדפן…
      </p>
    );
  }
  if (!boundary) {
    return (
      <p data-testid="bws-boundary" className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
        {healthFailed
          ? "מצב מתאם הדפדפן לא נטען מהשרת — לא ניתן לקבוע אם ביצוע דפדפן אפשרי. כשל־סגור: שום מערכת אינה מוצגת כזמינה לביצוע."
          : "השרת לא דיווח על מתאם דפדפן מורשה — ביצוע דפדפן חסום עד שיאושר מתאם כזה. אין מתאם דפדפן בכלי ההרצה המדווחים."}
      </p>
    );
  }
  const allowed = Array.isArray(boundary.allowed_systems) ? boundary.allowed_systems.filter((s) => typeof s === "string").slice(0, 8) : null;
  const blockedReason = boundedText(boundary.blocked_reason, 200);
  const toolPolicy = boundedText(boundary.tool_policy, 80);
  const approvalPermission = boundedText(boundary.approval_permission, 80);
  // The bounded DEV/lab transport (FULL-0105), reported by `browser_worker.dev_fixture_transport`.
  // It is a REAL transport -- a real HTTP connection to a loopback read-only fixture -- but it is
  // not live execution against a customer target, so it is never folded into execution_available.
  // Only a reported `available === true` renders anything: an absent key, a non-object or any other
  // value is the DEV path being off, which is the production default and needs no announcement.
  // Without this line the panel contradicts the product: with the DEV transport on, live execution
  // is still unusable, so the strip above reads "ביצוע דפדפן חסום" while a DEV-backed session can
  // in fact run.
  const devTransport = boundary.dev_fixture_transport && typeof boundary.dev_fixture_transport === "object"
    ? boundary.dev_fixture_transport
    : null;
  const devTransportId = devTransport ? boundedText(devTransport.transport, 60) : null;
  return (
    <div data-testid="bws-boundary" className="text-xs bg-gray-50 border border-gray-200 rounded px-3 py-2 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        {boundary.execution_available === true && (
          <span className="px-1.5 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 font-medium">▶ קריאה חיה זמינה — קריאה בלבד (read-only)</span>
        )}
        {boundary.execution_available === false && (
          <span className="px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200 font-medium">ביצוע דפדפן חסום</span>
        )}
        {typeof boundary.execution_available !== "boolean" && (
          <span className="px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-200">מצב המתאם לא דווח</span>
        )}
        {boundary.approval_required === true && (
          <span className="text-gray-600">
            נדרש אישור לפני ביצוע{approvalPermission ? <> (הרשאה: <span className="font-mono" dir="ltr">{approvalPermission}</span>)</> : null}
          </span>
        )}
        {boundary.approval_required === false && (
          <span data-testid="bws-approval-not-required" className="text-gray-600">אישור לפני ביצוע: לא נדרש לפי דיווח השרת</span>
        )}
        {typeof boundary.approval_required !== "boolean" && (
          <span data-testid="bws-approval-unknown" className="px-1.5 py-0.5 rounded border bg-gray-100 text-gray-600 border-gray-200">שער האישור לא דווח</span>
        )}
        {toolPolicy && <span className="text-gray-500">מדיניות כלים: <span className="font-mono" dir="ltr">{toolPolicy}</span></span>}
      </div>
      <div data-testid="bws-write-boundary" className="text-gray-600">{WRITE_BOUNDARY_LINE}</div>
      <div data-testid="bws-rollback-boundary" className="text-gray-600">{ROLLBACK_BOUNDARY_LINE}</div>
      {devTransport && devTransport.available === true && (
        <div data-testid="bws-dev-fixture-transport" className="text-gray-600">
          מסלול DEV/מעבדה: תעבורת דפדפן אמיתית מול מתקן קריאה־בלבד על loopback — אינו ביצוע חי מול לקוח ואינו פוטר מאישור.
          {devTransportId ? <>{" ("}<span className="font-mono" dir="ltr">{devTransportId}</span>{")"}</> : null}
        </div>
      )}
      {blockedReason && (
        <div data-testid="bws-blocked-reason" className="text-gray-600">
          סיבת החסימה: {browserCapabilityBlockedLabel(boundary.blocked_reason)}
          {" ("}<span className="font-mono" dir="ltr">{blockedReason}</span>{")"}
        </div>
      )}
      <div className="text-gray-600">
        מערכות מותרות לעבודת דפדפן: {allowed && allowed.length > 0
          ? allowed.map((s) => <span key={s} className="inline-block ml-1 px-1.5 rounded border bg-white">{s}</span>)
          : <span className="text-gray-500">לא דווח על-ידי השרת</span>}
      </div>
    </div>
  );
}

function StageHistory({ stages }) {
  if (!Array.isArray(stages) || stages.length === 0) {
    return (
      <p className="text-[11px] text-gray-400">
        היסטוריית השלבים לא דווחה על-ידי השרת — מוצג השלב הנוכחי בלבד.
      </p>
    );
  }
  const rows = stages
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => {
      const stage = norm(entry.stage) || norm(entry.state) || norm(entry.id) || norm(entry.key);
      const label = STAGE_META[stage]?.label || boundedText(entry.label, 40) || (stage ? stage.slice(0, 40) : null);
      const at = fmt(entry.at) || fmt(entry.timestamp);
      const rawNote = boundedText(entry.note, 120) || boundedText(entry.detail, 120);
      // `execution_blocked` is the one stage whose note is NOT prose: the session view model puts
      // the request-time capability `blocked_reason` there when no run was ever created
      // (browser_work_sessions._session_record), so this row printed the same English token the
      // boundary notice beside it used to. Every other stage's note stays as delivered — those are
      // sentences, and this is the only stage whose note has a machine producer.
      const note = stage === "execution_blocked" && rawNote ? browserCapabilityBlockedLabel(rawNote) : rawNote;
      return { label, at, note };
    })
    .filter((row) => row.label || row.at || row.note)
    .slice(0, 8);
  if (rows.length === 0) {
    return (
      <p className="text-[11px] text-gray-400">
        היסטוריית השלבים לא כללה שדות שאפשר להציג — מוצג השלב הנוכחי בלבד.
      </p>
    );
  }
  return (
    <ol data-testid="bws-stage-history" className="text-[11px] text-gray-600 space-y-0.5 border-r-2 border-gray-100 pr-2">
      {rows.map((row, i) => (
        <li key={i} className="flex flex-wrap items-center gap-x-2">
          <span className="font-medium">{row.label || "שלב ללא תווית"}</span>
          {row.note && <span className="text-gray-500">{row.note}</span>}
          {row.at && <span className="text-gray-400 mr-auto">{row.at}</span>}
        </li>
      ))}
    </ol>
  );
}

function EvidenceBlock({ session }) {
  const execution = session.execution && typeof session.execution === "object" ? session.execution : {};
  const updatedAt = fmt(session.updated_at);
  const blockedReason = boundedText(execution.blocked_reason, 200);
  const toolPolicy = boundedText(execution.tool_policy, 80);
  const fallback = boundedText(execution.fallback, 40);
  const verification = boundedText(execution.verification, 60);
  // Credential lifecycle: booleans and a count only — the surface's own contract is that no
  // credential value ever exists here, so nothing but a literal boolean/number is rendered.
  const credential = execution.credential && typeof execution.credential === "object" ? execution.credential : null;
  const credentialParts = credential ? [
    credential.received === true ? "נמסרו לסשן" : credential.received === false ? "לא נמסרו" : null,
    credential.consumed === true ? "נצרכו פעם אחת" : credential.consumed === false ? "לא נצרכו" : null,
    typeof credential.purged === "number" && Number.isFinite(credential.purged) ? `נמחקו מהמאגר הזמני: ${credential.purged}` : null,
  ].filter(Boolean) : [];
  const requestedBy = boundedText(session.requested_by, 80);
  const requestedAt = fmt(session.requested_at);
  const evidenceRows = Array.isArray(session.evidence)
    ? session.evidence
        .filter((e) => e && typeof e === "object")
        .map((e) => ({ label: boundedText(e.label, 60), value: boundedText(e.value, 160), at: fmt(e.at) }))
        .filter((e) => e.label || e.value || e.at)
        .slice(0, 6)
    : [];
  const rows = [
    requestedAt && { key: "req-at", node: <>נשלחה: {requestedAt}</> },
    requestedBy && { key: "req-by", node: <>מבקש: <span dir="ltr">{requestedBy}</span></> },
    fallback && fallback !== "none" && { key: "fb", node: <>נפילה חלופית: <span className="font-mono" dir="ltr">{fallback}</span></> },
    fallback === "none" && { key: "fb-none", node: "אין נפילה חזרה ל-API (fallback: none)" },
    verification && { key: "verif", node: <>אימות-בדיעבד: <span className="font-mono" dir="ltr">{verification}</span></> },
    credentialParts.length > 0 && { key: "cred-lifecycle", node: <>מחזור חיי אישורי התחברות (ללא ערכים): {credentialParts.join(" · ")}</> },
    blockedReason && { key: "blocked", node: <>חסימת ביצוע: {browserExecutionReasonLabel(blockedReason)}</> },
    toolPolicy && { key: "policy", node: <>מדיניות כלים: <span className="font-mono" dir="ltr">{toolPolicy}</span></> },
    updatedAt && { key: "upd", node: <>עודכן: {updatedAt}</> },
  ].filter(Boolean);

  if (rows.length === 0 && evidenceRows.length === 0) {
    return <p data-testid="bws-evidence" className="text-[11px] text-gray-400">אין ראיות מתועדות לסשן זה.</p>;
  }
  return (
    <div data-testid="bws-evidence" className="text-[11px] text-gray-600 space-y-0.5">
      {rows.map((row) => <div key={row.key}>{row.node}</div>)}
      {evidenceRows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-x-2">
          {row.label && <span className="font-medium">{row.label}</span>}
          {row.value && <span>{row.value}</span>}
          {row.at && <span className="text-gray-400 mr-auto">{row.at}</span>}
        </div>
      ))}
    </div>
  );
}

function WaitingBanner({ kind, rawWaiting, session, runId }) {
  if (!kind) {
    if (!rawWaiting) return null;
    return (
      <p data-testid="bws-waiting" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
        ממתין: <span dir="ltr">{rawWaiting.slice(0, 40)}</span> (סוג ההמתנה לא מזוהה — מוצג כפי שנמסר)
      </p>
    );
  }
  const execution = session.execution && typeof session.execution === "object" ? session.execution : {};
  const approvalPermission = boundedText(execution.approval_permission, 80);
  return (
    <div data-testid="bws-waiting" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2 space-y-1">
      <div className="font-medium">⏳ {WAITING_HE[kind]}</div>
      {kind === "approval" && (
        approvalPermission
          ? <div>הרשאה נדרשת: <span className="font-mono" dir="ltr">{approvalPermission}</span></div>
          : <div>ההרשאה הנדרשת לא דווחה על-ידי השרת.</div>
      )}
      {kind === "approval" && (runId
        ? <div><Link to={`/agent-runs/${runId}`} className="underline hover:text-amber-900">פתח את משטח הריצה לאישור</Link></div>
        : <div>לא קושרה ריצה לסשן זה — אישור מתבצע במשטח הריצה כשזו תיווצר.</div>)}
      {kind === "user" && <div>פעולות אינטראקטיביות מתבצעות במסגרת החיבור המאובטח (TeamViewer).</div>}
      {kind === "credential" && (
        <div>
          מסירת האישורים מתבצעת ידנית על-ידי מפעיל מורשה בלבד; הערכים נשמרים בזיכרון הזמני של
          הסשן, נצרכים פעם אחת ונמחקים בסיום — ואינם מוצגים במשטח זה.
        </div>
      )}
    </div>
  );
}

// The write-readiness outcome strip: the session's own stored execution facts, decoded into the
// operator-facing verdict. Every branch is grounded in the projected execution block — success /
// verification name what backed a success claim, and the refusal literals name exactly which arm
// refused. A combination the panel cannot ground renders nothing here; the raw bounded fields
// stay in the evidence block, never inferred into a success or into "nothing happened".
function SessionOutcome({ execution }) {
  if (!execution || typeof execution !== "object") return null;
  const success = execution.success;
  const verification = boundedText(execution.verification, 60);
  const blockedReason = boundedText(execution.blocked_reason, 160);

  if (blockedReason === REASON_LIVE_WRITE_ARM_UNAVAILABLE) {
    return (
      <div data-testid="bws-outcome-write-blocked" className="text-xs bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 space-y-0.5">
        <div className="font-medium">⛔ כתיבה חסומה</div>
        <div>אין מסלול כתיבה חי — הפעולה נעצרה לפני כל מגע עם היעד; לא בוצע שינוי.</div>
      </div>
    );
  }
  if (success === true) {
    if (verification === VERIFICATION_POSTCONDITION_READ_BACK) {
      return (
        <div data-testid="bws-outcome-verified-write" className="text-xs bg-green-50 border border-green-200 text-green-800 rounded px-3 py-2 space-y-0.5">
          <div className="font-medium">✓ הצלחה מאומתת — פעולה משנה־מצב</div>
          <div>השינוי אומת בקריאה חוזרת מהיעד. {ROLLBACK_BOUNDARY_LINE}</div>
        </div>
      );
    }
    if (verification === VERIFICATION_PROVIDER_RUN) {
      return (
        <div data-testid="bws-outcome-verified-read" className="text-xs bg-green-50 border border-green-200 text-green-800 rounded px-3 py-2 space-y-0.5">
          <div className="font-medium">✓ הצלחה מאומתת — קריאה בלבד</div>
          <div>בדיקת הקריאה אומתה בהרצת הספק; לא בוצע שינוי ביעד.</div>
        </div>
      );
    }
    return (
      <div data-testid="bws-outcome-verified-unknown" className="text-xs bg-green-50 border border-green-200 text-green-800 rounded px-3 py-2 space-y-0.5">
        <div className="font-medium">✓ הצלחה מדווחת</div>
        <div>סוג האימות לא דווח — לא ניתן לקבוע אם בוצע שינוי ביעד. {ROLLBACK_BOUNDARY_LINE}</div>
      </div>
    );
  }
  if (success === false) {
    const mismatch = blockedReason === REASON_POSTCONDITION_MISMATCH;
    return (
      <div data-testid="bws-outcome-verification-failed" className="text-xs bg-red-50 border border-red-200 text-red-800 rounded px-3 py-2 space-y-0.5">
        <div className="font-medium">{mismatch ? "✗ האימות לא עבר" : "✗ הסשן הסתיים ללא אימות הצלחה"}</div>
        {mismatch
          ? <div>הקריאה החוזרת לא תאמה את הפעולה המאושרת לאחר ביצועה — ייתכן שהשינוי בוצע ואינו מאומת. יש לאמת ידנית מול הראיות. {ROLLBACK_BOUNDARY_LINE}</div>
          : <div>דבר אינו מדווח כמוצלח. {ROLLBACK_BOUNDARY_LINE}</div>}
      </div>
    );
  }
  return null;
}

function SessionCard({ session, canApprove, agentId }) {
  const qc = useQueryClient();
  const stage = sessionStage(session);
  const terminal = TERMINAL_STAGES.has(stage);
  const { runId, runStatus } = runRefOf(session);
  const rawWaiting = norm(session.waiting_for) || norm(session.waiting?.for);
  const waitingKind = WAITING_KINDS[stage] || WAITING_KINDS[rawWaiting] || null;

  const ticketNumber = boundedNumber(session.ticket?.ticket_number) ?? boundedNumber(session.ticket_number);
  const ticketId = boundedText(session.ticket?.id, 64) || boundedText(session.ticket_id, 64);
  const customerName = boundedText(session.customer?.name, 80);
  const employeeName = boundedText(session.employee?.name, 80);
  const allowedSystem = boundedText(session.allowed_system, 120);
  const objectiveText = boundedText(session.objective?.text, 300);
  const redactions = boundedNumber(session.objective?.redactions);
  const conversationId = boundedText(session.conversation_id, 64) || boundedText(session.conversation?.id, 64);
  const handoff = session.handoff_readiness && typeof session.handoff_readiness === "object" ? session.handoff_readiness
    : session.handoff && typeof session.handoff === "object" ? session.handoff : null;
  const handoffReady = typeof handoff?.ready === "boolean" ? handoff.ready : typeof handoff?.available === "boolean" ? handoff.available : null;
  const handoffNote = boundedText(handoff?.note, 160) || boundedText(handoff?.detail, 160);

  const stopMutation = useMutation({
    mutationFn: () => agentRunsApi.stop(runId, "עצירת סשן עבודת דפדפן מהמשטח"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-workspace", agentId] });
      qc.invalidateQueries({ queryKey: ["agentRuns", "agent", agentId] });
      qc.invalidateQueries({ queryKey: ["agent", agentId] });
    },
  });

  const showStopButton = !terminal && Boolean(runId) && canApprove && (runStatus ? RUN_STOPPABLE.has(runStatus) : false);
  const stopErrorDetail = typeof stopMutation.error?.response?.data?.detail === "string"
    ? stopMutation.error.response.data.detail.slice(0, 200) : null;

  return (
    <div data-testid="bws-session-card" className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <StageChip stage={stage} />
        <span className="text-[11px] px-2 py-0.5 rounded-full border bg-white text-gray-600 border-gray-200">מצב מפורש: דפדפן</span>
        {session.api_fallback === false && (
          <span className="text-[11px] text-gray-400">ללא נפילה חזרה ל-API</span>
        )}
        <span className="flex-1" />
        {!terminal && runId && (
          <Link to={`/agent-runs/${runId}`} className="text-[11px] text-blue-600 hover:underline">צפה בריצה המקושרת</Link>
        )}
      </div>

      <SessionOutcome execution={session.execution} />

      <WaitingBanner kind={waitingKind} rawWaiting={!waitingKind && rawWaiting ? rawWaiting : null} session={session} runId={runId} />

      <div data-testid="bws-target" className="text-xs text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
        {employeeName && <div>עובד: <span className="font-medium">{employeeName}</span></div>}
        <div>
          קריאה: {ticketNumber != null
            ? (ticketId
                ? <Link to={`/tickets/${ticketId}`} className="text-blue-600 hover:underline">#{ticketNumber}</Link>
                : <span>#{ticketNumber}</span>)
            : <span className="text-gray-400">לא צוינה</span>}
        </div>
        <div>לקוח: {customerName ? <span className="font-medium">{customerName}</span> : <span className="text-gray-400">לא דווח</span>}</div>
        <div>מערכת מותרת: {allowedSystem ? allowedSystem : <span className="text-gray-400">לא צוינה</span>}</div>
        <div className="md:col-span-2">
          מטרה (כפי שנשמרה בשרת, מסוננת): {objectiveText
            ? <span>{objectiveText}{redactions > 0 && <span className="text-amber-700"> · הוסתרו {redactions} מקטעים</span>}</span>
            : <span className="text-gray-400">לא תועדה מטרה</span>}
        </div>
      </div>

      <StageHistory stages={session.stages} />
      <EvidenceBlock session={session} />

      {!terminal && (
        <div data-testid="bws-controls" className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
          {showStopButton && (
            <button
              type="button"
              data-testid="bws-stop"
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
              className="text-xs border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 rounded px-3 py-1"
            >
              {stopMutation.isPending ? "עוצר…" : "עצור סשן"}
            </button>
          )}
          {!terminal && runId && canApprove && runStatus && !RUN_STOPPABLE.has(runStatus) && (
            <span className="text-[11px] text-gray-500">הריצה המקושרת במצב <span dir="ltr">{runStatus}</span> — אינה ניתנת לעצירה מהמשטח.</span>
          )}
          {!terminal && runId && canApprove && !runStatus && (
            <span className="text-[11px] text-gray-500">מצב הריצה המקושרת לא דווח — עצירה תתבצע ממשטח הריצה.</span>
          )}
          {!terminal && !canApprove && runId && (
            <span className="text-[11px] text-gray-500">עצירת סשן דורשת הרשאת אישור (agents_approve).</span>
          )}
          {!terminal && !runId && (
            <span className="text-[11px] text-gray-500">אין ריצה מקושרת לסשן זה — לא ניתן לעצור אותו מהמשטח.</span>
          )}
          {stopMutation.isSuccess && (
            <span className="text-[11px] text-green-700">בקשת העצירה נשלחה.</span>
          )}
          {stopMutation.isError && (
            <span role="alert" className="text-[11px] text-red-600">{stopErrorDetail || "העצירה נכשלה."}</span>
          )}
          <span className="flex-1" />
          {conversationId && (
            <Link to={`/conversations/${conversationId}`} className="text-[11px] text-blue-600 hover:underline">משטח השיחה (העברה לטכנאי)</Link>
          )}
          {runId && (
            <Link to={`/agent-runs/${runId}`} className="text-[11px] text-blue-600 hover:underline">משטח הריצה (התערבות טכנאי)</Link>
          )}
          {handoffReady != null && (
            <span className={`text-[11px] px-1.5 py-0.5 rounded border ${handoffReady ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-gray-100 text-gray-600 border-gray-200"}`}>
              מוכנות העברה: {handoffReady ? "זמינה" : "לא זמינה"} (לפי השרת)
            </span>
          )}
          {handoffNote && <span className="text-[11px] text-gray-500">{handoffNote}</span>}
          <span className="text-[11px] text-gray-400">השתלטות מרחוק (TeamViewer) מתבצעת במנגנון הקיים — אין בקרת השתלטות חדשה כאן.</span>
        </div>
      )}
    </div>
  );
}

export default function BrowserWorkSessionPanel({ agentId, workspace }) {
  const { can: canAgent } = useAgentPermissions();
  const canApprove = canAgent("agents_approve");

  const { data: health, isError: healthFailed, isLoading: healthLoading } = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: () => {
      if (typeof connectionsApi?.health !== "function") throw new Error("connections health API unavailable");
      return connectionsApi.health();
    },
    staleTime: 60_000,
    retry: false,
  });
  const boundary = health?.browser_worker && typeof health.browser_worker === "object" ? health.browser_worker : null;

  const { sessions } = extractSessions(workspace);
  const visible = sessions ? sessions.slice(0, 5) : null;

  return (
    <section dir="rtl" aria-label="סשנים של עבודת דפדפן" data-testid="bws-section" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">סשנים של עבודת דפדפן</h3>
        <span data-testid="bws-mode-chip" className="text-[11px] px-2 py-0.5 rounded-full border bg-indigo-50 text-indigo-700 border-indigo-200 font-medium">מצב מפורש בלבד</span>
      </div>
      <p className="text-[11px] text-gray-400">
        עבודת דפדפן נדלקת רק לבקשה מפורשת מטכנאי מורשה ונקשרת לעובד, לקריאה וללקוח — אין הפעלה
        משתמעת ואין נפילה שקטה לנתיב ה-API. כל ביצוע מחייב אישור אנושי נפרד (agents_approve)
        לפניו; הסוכן אינו מאשר פעולות לעצמו.
      </p>

      <BoundarySection boundary={boundary} healthFailed={healthFailed} healthLoading={healthLoading} />

      <div className="space-y-2">
        {visible === null && (
          <p data-testid="bws-unknown-source" className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded px-3 py-2">
            מקור הסשנים טרם נחשף בגרסת השרת הזו — לא ניתן לקבוע אם קיימים סשני עבודת דפדפן לעובד זה.
          </p>
        )}
        {visible !== null && visible.length === 0 && (
          <p data-testid="bws-empty" className="text-xs text-gray-400 italic">לא נרשמו סשני עבודת דפדפן לעובד זה.</p>
        )}
        {visible !== null && visible.length > 0 && (
          <>
            {visible.map((session, i) => (
              <SessionCard key={boundedText(session.request_id, 64) || boundedText(session.id, 64) || i} session={session} canApprove={canApprove} agentId={agentId} />
            ))}
            {sessions.length > visible.length && (
              <p className="text-[11px] text-gray-400">מוצגים {visible.length} הסשנים האחרונים מתוך {sessions.length}.</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}
